import { PaymentMethodsCard } from "@/components/booking/PaymentMethodsCard";
import React, { useEffect, useState, useRef } from "react";
import { api } from "@/lib/http";
import { CURRENCIES, fmtCurrency } from "@/lib/calc";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Save, Building2, MapPin, Share2, CreditCard, CheckCircle2, MessageSquare, CalendarX, Gift, AlertTriangle, LineChart, HelpCircle } from "lucide-react";
import { toast } from "sonner";
const INCLUDED_SMS = 360;

const Field = ({ label, ...p }) => (
  <div className="space-y-1"><label className="text-xs text-muted-foreground">{label}</label><Input className="h-11 rounded-lg" {...p} /></div>
);
const ROOT = process.env.REACT_APP_BACKEND_URL;
const logoSrc = (u) => (u && u.startsWith("/") ? ROOT + u : u);

const OH_DAYS = [["mon", "Monday"], ["tue", "Tuesday"], ["wed", "Wednesday"], ["thu", "Thursday"], ["fri", "Friday"], ["sat", "Saturday"], ["sun", "Sunday"]];
const OH_DEFAULT = Object.fromEntries(OH_DAYS.map(([k]) => [k, { open: !["sat", "sun"].includes(k), start: "09:00", end: "17:00" }]));

export default function BusinessInfo() {
  const [f, setF] = useState({ name: "", abn: "", website: "", phone: "", contact_email: "", region: "", currency: "AUD", logo_url: "", instagram: "", facebook: "", tiktok: "", locations: [], address: "", cancellation_policy: "", deposit_enabled: false, deposit_forfeit_on_no_show: false, deposit_type: "percentage", deposit_value: "", sms_enabled: false, sms_sender_id: "", sms_price_per_msg: "", reminder_hours: 24, sms_credit_balance: 0, closed_dates: [], operating_hours: OH_DEFAULT, legacy_discount_type: "none", legacy_discount_value: "" });
  const [newClosedDate, setNewClosedDate] = useState("");
  const [newClosedDateEnd, setNewClosedDateEnd] = useState("");
  const [saving, setSaving] = useState(false);
  const [stripe, setStripe] = useState({ connected: false, charges_enabled: false });
  const [connecting, setConnecting] = useState(false);
  const [smsTopup, setSmsTopup] = useState("");
  const logoInput = useRef(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploadingLogo(true);
    try {
      const { data } = await api.post("/business/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((s) => ({ ...s, logo_url: data.logo_url }));
      toast.success("Logo uploaded");
    } catch (err) { toast.error(err?.response?.data?.detail || "Couldn't upload logo"); }
    finally { setUploadingLogo(false); if (logoInput.current) logoInput.current.value = ""; }
  };
  const addSmsCredit = async (amount) => {
    const amt = parseFloat(amount); if (!amt || amt <= 0) return;
    try { const { data } = await api.post("/sms/credit", { amount: amt }); setF((s) => ({ ...s, sms_credit_balance: data.sms_credit_balance })); setSmsTopup(""); toast.success("SMS credit added"); }
    catch { toast.error("Couldn't add credit"); }
  };
  const topUpSms = () => addSmsCredit(smsTopup);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const loadStripe = () => api.get("/stripe/connect/status").then(({ data }) => setStripe(data)).catch(() => {});
  const [speeding, setSpeeding] = useState(false);
  const fastestPayouts = async () => {
    setSpeeding(true);
    try {
      const { data } = await api.post("/stripe/connect/payout-speed");
      setStripe((s) => ({ ...s, payout_schedule: data.payout_schedule }));
      const d = data.payout_schedule?.delay_days;
      toast.success(d ? `Payouts now daily, ${d} business day${d === 1 ? "" : "s"} after each payment — the fastest Stripe allows for your account.` : "Payout schedule updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update payout speed"); }
    finally { setSpeeding(false); }
  };

  useEffect(() => {
    api.get("/business").then(({ data }) => setF((s) => ({ ...s, ...data, locations: data.locations || [], closed_dates: data.closed_dates || [], operating_hours: { ...OH_DEFAULT, ...(data.operating_hours || {}) } }))).catch(() => {});
    loadStripe();
    const params = new URLSearchParams(window.location.search);
    if (params.get("stripe")) {
      loadStripe();
      if (params.get("stripe") === "return") toast.success("Stripe onboarding updated");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const connectStripe = async () => {
    setConnecting(true);
    try {
      const { data } = await api.post("/stripe/connect/onboarding", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't start Stripe onboarding", { duration: 12000 }); setConnecting(false); }
  };

  const addLoc = () => setF((s) => ({ ...s, locations: [...s.locations, { name: "", address: "" }] }));
  const updLoc = (i, k, v) => setF((s) => ({ ...s, locations: s.locations.map((x, j) => j === i ? { ...x, [k]: v } : x) }));
  const rmLoc = (i) => setF((s) => ({ ...s, locations: s.locations.filter((_, j) => j !== i) }));
  const addClosedDate = () => {
    if (!newClosedDate) return;
    const end = newClosedDateEnd && newClosedDateEnd >= newClosedDate ? newClosedDateEnd : newClosedDate;
    const ymd = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
    const dates = [];
    let d = new Date(newClosedDate + "T00:00:00");
    const last = new Date(end + "T00:00:00");
    while (d <= last && dates.length < 366) { dates.push(ymd(d)); d.setDate(d.getDate() + 1); }
    setF((s) => ({ ...s, closed_dates: [...new Set([...(s.closed_dates || []), ...dates])].sort() }));
    setNewClosedDate(""); setNewClosedDateEnd("");
  };
  const rmClosedDate = (d) => setF((s) => ({ ...s, closed_dates: (s.closed_dates || []).filter((x) => x !== d) }));
  const setOH = (day, patch) => setF((s) => ({ ...s, operating_hours: { ...(s.operating_hours || OH_DEFAULT), [day]: { ...((s.operating_hours || OH_DEFAULT)[day]), ...patch } } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/business", {
        name: f.name, abn: f.abn, website: f.website, phone: f.phone, contact_email: f.contact_email,
        region: f.region, currency: f.currency, logo_url: f.logo_url, instagram: f.instagram,
        facebook: f.facebook, tiktok: f.tiktok, locations: f.locations,
        address: f.address, cancellation_policy: f.cancellation_policy,
        sms_enabled: !!f.sms_enabled, sms_my_bookings_link: f.sms_my_bookings_link !== false, sms_sender_id: f.sms_sender_id, sms_price_per_msg: parseFloat(f.sms_price_per_msg) || 0, reminder_hours: parseInt(f.reminder_hours) || 24,
        deposit_enabled: !!f.deposit_enabled, deposit_forfeit_on_no_show: !!f.deposit_forfeit_on_no_show, deposit_type: f.deposit_type || "percentage",
        deposit_value: parseFloat(f.deposit_value) || 0, closed_dates: f.closed_dates || [], operating_hours: f.operating_hours || OH_DEFAULT,
        legacy_discount_type: f.legacy_discount_type || "none", legacy_discount_value: parseFloat(f.legacy_discount_value) || 0,
        ga4_id: (f.ga4_id || "").trim(), gtm_id: (f.gtm_id || "").trim(), meta_pixel_id: (f.meta_pixel_id || "").trim(), google_ads_id: (f.google_ads_id || "").trim(),
      });
      toast.success("Business info saved");
    } catch { toast.error("Couldn't save"); } finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="font-heading font-extrabold text-2xl sm:text-3xl tracking-tight">Business info</h1>
            <a href="/help/marketing-tracking" target="_blank" rel="noreferrer" data-testid="help-business-info" title="Help: Marketing & tracking" className="text-muted-foreground hover:text-primary transition-colors"><HelpCircle className="h-5 w-5" /></a>
          </div>
          <p className="text-muted-foreground mt-1">Your details, locations, branding & regional settings</p>
        </div>
        <Button className="rounded-lg" onClick={save} disabled={saving} data-testid="business-save"><Save className="h-4 w-4 mr-1.5" />{saving ? "Saving…" : "Save"}</Button>
      </div>

      <Card className="rounded-2xl border bg-card p-5 shadow-sm space-y-3">
        <div className="flex items-center gap-2 mb-1"><Building2 className="h-4 w-4 text-primary" /><h3 className="font-heading font-semibold">Details</h3></div>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Business name" value={f.name || ""} onChange={(e) => set("name", e.target.value)} data-testid="bi-name" />
          <Field label="ABN / Tax number" value={f.abn || ""} onChange={(e) => set("abn", e.target.value)} data-testid="bi-abn" />
          <Field label="Website" value={f.website || ""} onChange={(e) => set("website", e.target.value)} placeholder="https://" data-testid="bi-website" />
          <Field label="Phone" value={f.phone || ""} onChange={(e) => set("phone", e.target.value)} data-testid="bi-phone" />
          <Field label="Contact email" value={f.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} data-testid="bi-email" />
          <div className="space-y-1 sm:col-span-2">
            <label className="text-xs text-muted-foreground">Premises address (shown on booking confirmations)</label>
            <Input className="h-11 rounded-lg" value={f.address || ""} onChange={(e) => set("address", e.target.value)} placeholder="e.g. 12 Beauty Lane, Bondi NSW 2026" data-testid="bi-address" />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Currency / billing region</label>
            <Select value={f.currency} onValueChange={(v) => set("currency", v)}>
              <SelectTrigger className="h-11 rounded-lg" data-testid="bi-currency"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(CURRENCIES).map(([code, c]) => <SelectItem key={code} value={code}>{code} — {c.symbol}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Field label="Region / timezone" value={f.region || ""} onChange={(e) => set("region", e.target.value)} placeholder="e.g. Australia/Sydney" data-testid="bi-region" />
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Clinic logo</label>
            <div className="flex items-center gap-3">
              <input ref={logoInput} type="file" accept="image/*" className="hidden" onChange={uploadLogo} data-testid="bi-logo-input" />
              <Button type="button" variant="outline" className="rounded-lg h-11" disabled={uploadingLogo} onClick={() => logoInput.current?.click()} data-testid="bi-logo-upload">{uploadingLogo ? "Uploading…" : "Upload logo"}</Button>
              {f.logo_url ? <img src={logoSrc(f.logo_url)} alt="logo" className="h-11 rounded border border-border" onError={(e) => { e.target.style.display = "none"; }} /> : <span className="text-xs text-muted-foreground">No logo yet</span>}
            </div>
          </div>
        </div>
      </Card>

      <Card className="rounded-2xl border bg-card p-5 shadow-sm space-y-3 mt-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-primary" /><h3 className="font-heading font-semibold">Locations</h3></div>
        </div>
        {f.locations.length === 0 && <p className="text-sm text-muted-foreground" data-testid="locations-empty">No locations yet.</p>}
        {f.locations.map((l, i) => (
          <div key={i} className="flex gap-2" data-testid={`location-${i}`}>
            <Input placeholder="Name (e.g. Main studio)" value={l.name} onChange={(e) => updLoc(i, "name", e.target.value)} className="h-10 rounded-lg w-48" data-testid={`location-name-${i}`} />
            <Input placeholder="Address" value={l.address} onChange={(e) => updLoc(i, "address", e.target.value)} className="h-10 rounded-lg flex-1" data-testid={`location-address-${i}`} />
            <button onClick={() => rmLoc(i)} className="h-10 w-10 rounded-lg border border-border flex items-center justify-center text-muted-foreground hover:text-destructive shrink-0" data-testid={`location-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        <button onClick={addLoc} className="text-sm text-primary flex items-center gap-1 hover:underline" data-testid="add-location"><Plus className="h-4 w-4" /> Add location</button>
      </Card>

      <Card className="rounded-2xl border bg-card p-5 shadow-sm space-y-3 mt-5" data-testid="closed-dates-card">
        <div className="flex items-center gap-2 mb-1"><CalendarX className="h-4 w-4 text-primary" /><h3 className="font-heading font-semibold">Closed dates</h3></div>
        <p className="text-xs text-muted-foreground -mt-1">Public holidays or clinic closures. Pick a single day, or a From–To range for a holiday period. Online booking won't offer any times on these days.</p>
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <label className="text-[11px] text-muted-foreground block mb-1">From</label>
            <Input type="date" value={newClosedDate} onChange={(e) => setNewClosedDate(e.target.value)} className="h-10 rounded-lg w-44" data-testid="closed-date-input" />
          </div>
          <div>
            <label className="text-[11px] text-muted-foreground block mb-1">To (optional)</label>
            <Input type="date" value={newClosedDateEnd} min={newClosedDate} onChange={(e) => setNewClosedDateEnd(e.target.value)} className="h-10 rounded-lg w-44" data-testid="closed-date-end-input" />
          </div>
          <Button variant="outline" size="sm" className="rounded-lg h-10" onClick={addClosedDate} data-testid="closed-date-add"><Plus className="h-4 w-4 mr-1" />Add</Button>
        </div>
        {(f.closed_dates || []).length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="closed-dates-empty">No closed dates set.</p>
        ) : (
          <div className="flex flex-wrap gap-2" data-testid="closed-dates-list">
            {(f.closed_dates || []).map((d) => (
              <span key={d} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-muted text-sm" data-testid={`closed-date-${d}`}>
                {d}
                <button onClick={() => rmClosedDate(d)} className="text-muted-foreground hover:text-destructive" data-testid={`closed-date-remove-${d}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </span>
            ))}
          </div>
        )}
      </Card>

      <Card className="p-6 rounded-2xl space-y-4" data-testid="operating-hours-card">
        <div className="flex items-center gap-2 mb-1"><CalendarX className="h-4 w-4 text-primary" /><h3 className="font-heading font-semibold">Operating hours</h3></div>
        <p className="text-xs text-muted-foreground -mt-1">Set the days and times your clinic is open. The calendar shows this time range, and online booking only offers times within your open hours.</p>
        <div className="space-y-2">
          {OH_DAYS.map(([key, label]) => {
            const d = (f.operating_hours || OH_DEFAULT)[key] || { open: false, start: "09:00", end: "17:00" };
            return (
              <div key={key} className="flex items-center gap-3 flex-wrap" data-testid={`oh-row-${key}`}>
                <div className="flex items-center gap-2 w-40">
                  <Switch checked={!!d.open} onCheckedChange={(v) => setOH(key, { open: v })} data-testid={`oh-open-${key}`} />
                  <span className={`text-sm ${d.open ? "font-medium" : "text-muted-foreground"}`}>{label}</span>
                </div>
                {d.open ? (
                  <div className="flex items-center gap-2 text-sm">
                    <Input type="time" value={d.start} onChange={(e) => setOH(key, { start: e.target.value })} className="h-9 rounded-lg w-28" data-testid={`oh-start-${key}`} />
                    <span className="text-muted-foreground">to</span>
                    <Input type="time" value={d.end} onChange={(e) => setOH(key, { end: e.target.value })} className="h-9 rounded-lg w-28" data-testid={`oh-end-${key}`} />
                  </div>
                ) : (
                  <span className="text-sm text-muted-foreground">Closed</span>
                )}
              </div>
            );
          })}
        </div>
      </Card>

      <Card className="rounded-2xl border bg-card p-5 shadow-sm space-y-3 mt-5">
        <div className="flex items-center gap-2 mb-1"><Share2 className="h-4 w-4 text-primary" /><h3 className="font-heading font-semibold">Socials</h3></div>
        <div className="grid sm:grid-cols-3 gap-3">
          <Field label="Instagram" value={f.instagram || ""} onChange={(e) => set("instagram", e.target.value)} placeholder="@handle" data-testid="bi-instagram" />
          <Field label="Facebook" value={f.facebook || ""} onChange={(e) => set("facebook", e.target.value)} data-testid="bi-facebook" />
          <Field label="TikTok" value={f.tiktok || ""} onChange={(e) => set("tiktok", e.target.value)} placeholder="@handle" data-testid="bi-tiktok" />
        </div>
      </Card>

      <Card className="rounded-2xl border bg-card p-5 shadow-sm space-y-3 mt-5" data-testid="cancellation-card">
        <div className="flex items-center gap-2 mb-1"><Building2 className="h-4 w-4 text-primary" /><h3 className="font-heading font-semibold">Cancellation policy</h3></div>
        <p className="text-xs text-muted-foreground">Clients must tick to acknowledge this when booking online. Leave blank to use the default 24-hour notice policy.</p>
        <textarea value={f.cancellation_policy || ""} onChange={(e) => set("cancellation_policy", e.target.value)} placeholder="We require at least 24 hours' notice to change or cancel your appointment. Late cancellations or no-shows may incur a fee." className="w-full rounded-lg border border-input bg-background p-3 text-sm min-h-[90px]" data-testid="bi-cancellation-policy" />
      </Card>

      <Card className="rounded-2xl border bg-card p-5 shadow-sm space-y-3 mt-5" data-testid="sms-card">
        <div className="flex items-center gap-2 mb-1"><MessageSquare className="h-4 w-4 text-primary" /><h3 className="font-heading font-semibold">SMS reminders & receipts</h3></div>
        <div className="flex items-center justify-between rounded-lg border border-border p-4">
          <div>
            <div className="font-medium text-sm">Send SMS (one-way, from your business name)</div>
            <div className="text-xs text-muted-foreground mt-0.5">Appointment reminders + gift-voucher receipts. Costs are drawn from your SMS credit.</div>
          </div>
          <Switch checked={!!f.sms_enabled} onCheckedChange={(v) => set("sms_enabled", v)} data-testid="sms-enabled-toggle" />
        </div>
        {f.sms_enabled && (
          <>
            <div className="flex items-center justify-between rounded-lg border border-border p-4" data-testid="sms-my-bookings-row">
              <div>
                <div className="font-medium text-sm">Add a “Your appointments” link to confirmations &amp; reminders</div>
                <div className="text-xs text-muted-foreground mt-0.5">A short link (~45 characters) to a no-login page listing the client's upcoming bookings — stops the “when is my appointment?” calls.</div>
              </div>
              <Switch checked={f.sms_my_bookings_link !== false} onCheckedChange={(v) => set("sms_my_bookings_link", v)} data-testid="sms-my-bookings-toggle" />
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              <Field label="Sender name (max 11 chars)" maxLength={11} value={f.sms_sender_id || ""} onChange={(e) => set("sms_sender_id", e.target.value)} placeholder="e.g. GlowSkin" data-testid="sms-sender-id" />
              <Field label="Price per SMS (charged as credit)" type="number" min="0" step="0.01" value={f.sms_price_per_msg} onChange={(e) => set("sms_price_per_msg", e.target.value)} placeholder="0.15" data-testid="sms-price" />
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">Send reminder before appointment</label>
                <Select value={String(f.reminder_hours || 48)} onValueChange={(v) => set("reminder_hours", v)}>
                  <SelectTrigger className="h-11 rounded-lg" data-testid="sms-reminder-hours"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="72">72 hours before</SelectItem>
                    <SelectItem value="48">48 hours before</SelectItem>
                    <SelectItem value="12">12 hours before</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="rounded-lg border border-border p-4" data-testid="sms-plan-info">
              <div className="text-sm font-medium">Texts are included in your plan</div>
              <p className="text-xs text-muted-foreground mt-1">{INCLUDED_SMS} texts are included each month. Any extra texts are billed at {fmtCurrency(parseFloat(f.sms_price_per_msg) || 0.12, f.currency, 2)} each on your end-of-month invoice — nothing to top up. See <b>Billing</b> for this month's usage.</p>
            </div>
            <p className="text-[11px] text-muted-foreground">A confirmation text is sent automatically when a booking is made, and a reminder is sent {String(f.reminder_hours || 48)} hours before. Live sending activates once your Twilio Trust Hub profile + sender ID are approved; until then messages are logged.</p>
          </>
        )}
      </Card>

      <PaymentMethodsCard />
      <Card className="rounded-2xl border bg-card p-5 shadow-sm space-y-4 mt-5" data-testid="deposits-card">
        <div className="flex items-center gap-2 mb-1"><CreditCard className="h-4 w-4 text-primary" /><h3 className="font-heading font-semibold">Online deposits & payments</h3></div>

        <div className="rounded-lg border border-border p-4 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="font-medium text-sm flex items-center gap-1.5">
              Stripe account
              {stripe.charges_enabled && <span className="inline-flex items-center gap-1 text-primary text-xs"><CheckCircle2 className="h-3.5 w-3.5" /> Connected</span>}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {stripe.charges_enabled
                ? "Deposits will settle straight into your own Stripe account."
                : stripe.connected
                  ? "Onboarding started but not complete — finish it to accept deposits."
                  : "Connect your Stripe account so deposits land in your bank, not ours."}
            </div>
          </div>
          <Button variant={stripe.charges_enabled ? "outline" : "default"} className="rounded-lg" onClick={connectStripe} disabled={connecting} data-testid="connect-stripe-button">
            {connecting ? "Opening Stripe…" : stripe.charges_enabled ? "Manage on Stripe" : stripe.connected ? "Finish onboarding" : "Connect Stripe"}
          </Button>
        </div>

        {stripe.charges_enabled && (
          <div className="rounded-lg border border-border p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="payout-speed-row">
            <div>
              <div className="font-medium text-sm">Payout speed</div>
              <div className="text-xs text-muted-foreground mt-0.5" data-testid="payout-speed-current">
                {stripe.payout_schedule?.delay_days
                  ? `Currently ${stripe.payout_schedule.interval || "daily"} payouts, ${stripe.payout_schedule.delay_days} business day${stripe.payout_schedule.delay_days === 1 ? "" : "s"} after each payment.`
                  : "Payout timing is set on your Azordi-connected Stripe account, not your other Stripe logins."}
                {" "}Stripe sets the floor for Australia (usually 2 business days); we can ask for the lowest it allows.
              </div>
            </div>
            <Button variant="outline" className="rounded-lg" onClick={fastestPayouts} disabled={speeding} data-testid="fastest-payouts-button">
              {speeding ? "Updating…" : "Use fastest payouts"}
            </Button>
          </div>
        )}

        <div className="flex items-center justify-between rounded-lg border border-border p-4">
          <div>
            <div className="font-medium text-sm">Require a deposit at online booking</div>
            <div className="text-xs text-muted-foreground mt-0.5">{stripe.charges_enabled ? "Applies to every online booking." : "Connect Stripe first to switch this on."}</div>
          </div>
          <Switch checked={!!f.deposit_enabled} onCheckedChange={(v) => set("deposit_enabled", v)} disabled={!stripe.charges_enabled} data-testid="deposit-enabled-toggle" />
        </div>

        {f.deposit_enabled && (
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Deposit type</label>
              <Select value={f.deposit_type || "percentage"} onValueChange={(v) => set("deposit_type", v)}>
                <SelectTrigger className="h-11 rounded-lg" data-testid="deposit-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage" data-testid="deposit-type-percentage">Percentage of price</SelectItem>
                  <SelectItem value="fixed" data-testid="deposit-type-fixed">Fixed amount</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Field label={f.deposit_type === "fixed" ? `Amount (${CURRENCIES[f.currency]?.symbol || "$"})` : "Percent (%)"} type="number" min="0" value={f.deposit_value} onChange={(e) => set("deposit_value", e.target.value)} data-testid="deposit-value" />
          </div>
        )}
        <div className="flex items-center justify-between gap-4 rounded-lg border p-3" data-testid="deposit-forfeit-row">
          <div>
            <div className="font-medium text-sm">Forfeit the deposit on a no-show</div>
            <div className="text-xs text-muted-foreground mt-0.5">Paid deposits sit in the client's file as credit. When this is on, marking a booking as no-show takes the deposit out of their credit and records it as clinic income. Off = the credit stays with the client.</div>
          </div>
          <Switch checked={!!f.deposit_forfeit_on_no_show} onCheckedChange={(v) => set("deposit_forfeit_on_no_show", v)} data-testid="deposit-forfeit-toggle" />
        </div>
        <p className="text-[11px] text-muted-foreground">Deposits are held as client credit and drawn down at in-clinic checkout, so you only charge the balance. Signed-in clients can use existing credit to cover all or part of a deposit online.</p>
      </Card>

      <Card className="rounded-2xl border bg-card p-5 shadow-sm space-y-4 mt-5" data-testid="tracking-card">
        <div className="flex items-center gap-2 mb-1"><LineChart className="h-4 w-4 text-primary" /><h3 className="font-heading font-semibold">Marketing & tracking</h3></div>
        <p className="text-[11px] text-muted-foreground -mt-2">Add your own analytics IDs. These load on <b>your public booking page</b> (/book/{f.slug || "your-clinic"}) and record page views plus a conversion when a client confirms a booking. Leave any field blank to switch it off. Changes go live after the app is redeployed.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Google Analytics 4 — Measurement ID" value={f.ga4_id || ""} onChange={(e) => set("ga4_id", e.target.value)} placeholder="G-XXXXXXXXXX" data-testid="bi-ga4" />
          <Field label="Google Tag Manager — Container ID" value={f.gtm_id || ""} onChange={(e) => set("gtm_id", e.target.value)} placeholder="GTM-XXXXXXX" data-testid="bi-gtm" />
          <Field label="Meta (Facebook) Pixel ID" value={f.meta_pixel_id || ""} onChange={(e) => set("meta_pixel_id", e.target.value)} placeholder="123456789012345" data-testid="bi-meta-pixel" />
          <Field label="Google Ads — Conversion ID" value={f.google_ads_id || ""} onChange={(e) => set("google_ads_id", e.target.value)} placeholder="AW-XXXXXXXXX" data-testid="bi-google-ads" />
        </div>
      </Card>

      <Card className="p-5 sm:p-6 rounded-2xl mt-6" data-testid="legacy-discount-card">
        <h2 className="font-heading font-bold text-lg mb-1 flex items-center gap-2"><Gift className="h-5 w-5 text-amber-500" />Legacy client pricing</h2>
        <p className="text-sm text-muted-foreground mb-4">Grandfathered pricing for long-standing clients. Toggle a client "Legacy" on their file and this discount is applied automatically at checkout.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Discount type</label>
            <Select value={f.legacy_discount_type || "none"} onValueChange={(v) => set("legacy_discount_type", v)}>
              <SelectTrigger className="h-11 rounded-lg" data-testid="legacy-discount-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No legacy discount</SelectItem>
                <SelectItem value="percentage">Percentage off</SelectItem>
                <SelectItem value="fixed">Fixed amount off</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {f.legacy_discount_type && f.legacy_discount_type !== "none" && (
            <Field label={f.legacy_discount_type === "fixed" ? `Amount off (${CURRENCIES[f.currency]?.symbol || "$"})` : "Percent off (%)"} type="number" min="0" value={f.legacy_discount_value} onChange={(e) => set("legacy_discount_value", e.target.value)} data-testid="legacy-discount-value" />
          )}
        </div>
      </Card>
    </div>
  );
}
